import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"
import { gql, useMutation, useQuery } from "@apollo/client"
import { Banner } from "../../util/Banner"
import tableStyle from "../../../style/table.module.css"
import { useParams } from "react-router-dom"
import { Flash, WARNING } from "../../util/Flash"
import { TableHeader } from "../../util/table/TableHeader"
import { TableContentLoader } from "../../util/table/TableContentLoader"
import { Pagination } from "../../util/table/Pagination"
import cn from "classnames"
import buttonStyle from "../../../style/button.module.css"
import inputStyle from "../../../style/input.module.css"
import dropDownStyle from "../../util/input/DropDownFilter.module.css"
import { useDispatch } from "react-redux"
import c from "capitalize"
import { showError, showInfo } from "../../../redux/actions/NotificationActions"
import { SmartFilter } from "../../util/input/SmartFilter"
import {
    getCurrencyFormatter,
    getDayMonthYearFormatter,
    getIntegerFormatter,
    getSafeFormatter,
} from "../../../i18n"
import { prisoBannerKey } from "./prisoUtils"

const GET_PRISO_RESULTS = gql`
    query (
        $id: UUID!
        $listId: UUID!
        $query: String
        $pageNum: Int
        $order: [PrisoResultByProductSearchOrder]
        $filter: PrisoResultProductSearchFilter
    ) {
        list: prisoList(id: $listId) {
            name
            id
            calculation: prisoCalculation(id: $id) {
                name
                id
                results: sortablePrisoResults(
                    limit: 20
                    pageNum: $pageNum
                    orderBy: $order
                    filterParams: $filter
                    query: $query
                ) {
                    currentPage
                    hasNext
                    hasPrevious
                    pages
                    suggestedQuery
                    page {
                        id
                        pzn
                        artikeltext
                        AEPPsNeu
                        GEPPsNeu
                        staffelpreis1Neu
                        menge1Neu
                        SEKNeu
                        agrosso1
                        AEP
                        GEP
                        validFrom
                        validUntil
                        product {
                            id
                            name
                            pzn
                        }
                        manualAdjustment {
                            id
                            AEPPsNeu
                            GEPPsNeu
                        }
                    }
                }
            }
        }
    }
`

const FILTER_PRISO_QUERY = gql`
    query getFilter($id: UUID!, $listId: UUID!) {
        list: prisoList(id: $listId) {
            name
            id
            calculation: prisoCalculation(id: $id) {
                name
                id
                results: sortablePrisoResults(limit: 0) {
                    filterFields {
                        name
                        dataType
                        displayName
                        options
                    }
                }
            }
        }
    }
`

const SET_PRISO_PRICES = gql`
    mutation ($changes: [ManualPrisoPriceInput]) {
        setPrice: setPrisoPrices(prisoAdjustments: $changes) {
            id
            manualAdjustment {
                id
                AEPPsNeu
                GEPPsNeu
            }
        }
    }
`

const EXPORT_PRISO_METRICS = gql`
    mutation send($id: UUID!) {
        send: downloadPrisoMetrics(id: $id)
    }
`

const SEND_PRISO_EMAIL = gql`
    mutation send($id: UUID!) {
        send: sendPrisoAsEmail(id: $id)
    }
`

export const PrisoResults = ({ prisoType }) => {
    const { listId, id } = useParams()
    const [pageNum, setPage] = useState(1)
    const [order, setOrder] = useState([])
    const [changes, setChanges] = useState({})
    const [query, setQuery] = useState("")
    const [filter, setFilter] = useState({})
    const [editRow, setEditRow] = useState(null)

    const { t, i18n } = useTranslation(["common", "table", "messages", "page_names"])

    const bannerKey = prisoBannerKey(prisoType)

    const { data, loading: queryLoading } = useQuery(GET_PRISO_RESULTS, {
        variables: {
            pageNum,
            order,
            filter,
            query,
            id,
            listId,
        },
    })

    const clampOrEmpty = (input, minValue, maxValue) =>
        input.trim()
            ? Math.max(minValue, Math.min(maxValue, +input.trim()))
            : ""

    const [setManualPrices, { loading: updateLoading }] =
        useMutation(SET_PRISO_PRICES)
    const [exportMetrics] = useMutation(EXPORT_PRISO_METRICS)
    const [sendEmail] = useMutation(SEND_PRISO_EMAIL)

    const loading = updateLoading || queryLoading

    const currencyFormat = getSafeFormatter(getCurrencyFormatter(i18n.language))
    const integerFormatter = getSafeFormatter(
        getIntegerFormatter(i18n.language),
    )
    const dateFormatter = getSafeFormatter(
        getDayMonthYearFormatter(i18n.language),
    )

    const dispatch = useDispatch()

    const canSendEmail =
        prisoType === "NEUANLAGE_KETTARTIKEL" || prisoType === "EK_AENDERUNG"

    return (
        <div>
            <Banner
                text={
                    loading
                        ? t(`page_names:priso_results`)
                        : `${t(`page_names:priso_results`)}:
                     ${data?.list?.name || ""}
                     - ${data?.list?.calculation?.name || ""}`
                }
            />
            {!loading && !data?.list ? (
                <Flash text={t("table:no_results")} type={WARNING} />
            ) : null}

            <div className={dropDownStyle.container}>
                <SmartFilter
                    filterFieldQuery={FILTER_PRISO_QUERY}
                    filterQueryOptions={{
                        variables: { id, listId },
                    }}
                    filterPath={(q) =>
                        q?.list?.calculation?.results?.filterFields
                    }
                    setQuery={setQuery}
                    setFilter={setFilter}
                    suggestedQuery={
                        data?.list?.calculation?.results?.suggestedQuery
                    }
                />

                <div className={tableStyle.buttonBar}>
                    <button
                        className={buttonStyle.button}
                        onClick={() =>
                            exportMetrics({
                                variables: { id },
                            })
                                .then(() =>
                                    dispatch(
                                        showInfo("common:export_triggered"),
                                    ),
                                )
                                .catch((e) => dispatch(showError(e.message)))
                        }
                    >
                        {t("table:all_metrics_download_csv")}
                    </button>
                    {canSendEmail ? (
                        <button
                            className={buttonStyle.button}
                            onClick={() =>
                                sendEmail({
                                    variables: { id },
                                })
                                    .then(() =>
                                        dispatch(
                                            showInfo("common:email_sent"),
                                        ),
                                    )
                                    .catch((e) =>
                                        dispatch(showError(e.message)),
                                    )
                            }
                        >
                            {c(t("table:send_email"))}
                        </button>
                    ) : null}
                </div>
            </div>
            <div className={dropDownStyle.container}>
                <div className={tableStyle.buttonBar}>
                    {Object.entries(changes).length > 0 && (
                        <>
                            <h3>
                                {t("table:number_of_unsaved_changes")}:{" "}
                                {Object.entries(changes).length}
                            </h3>

                            <button
                                className={buttonStyle.button}
                                onClick={() => {
                                    setManualPrices({
                                        variables: {
                                            changes: Object.entries(
                                                changes,
                                            ).map((c) => ({
                                                id: c[0],
                                                AEPPsNeu:
                                                    Number.parseFloat(
                                                        c[1]?.aepPsNeu,
                                                    ) ?? null,
                                                GEPPsNeu:
                                                    Number.parseFloat(
                                                        c[1]?.gepPsNeu,
                                                    ) ?? null,
                                            })),
                                        },
                                    })
                                        .then(() => setChanges({}))
                                        .catch((e) =>
                                            dispatch(showError(e.message)),
                                        )
                                }}
                            >
                                {updateLoading ? "..." : c(t("common:save"))}
                            </button>

                            <button
                                className={cn(
                                    buttonStyle.button,
                                    buttonStyle.dangerButton,
                                )}
                                onClick={() => setChanges({})}
                            >
                                {c(t("common:reset"))}
                            </button>
                        </>
                    )}
                </div>
            </div>
            <div className={tableStyle.wrapper}>
                <div
                    className={tableStyle.table}
                    style={{
                        gridTemplateColumns:
                            "1fr 2fr 2fr repeat(2, 1fr) 1fr 1fr 1fr 1fr 1fr 1fr",
                    }}
                >
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:pzn")}
                        order={order}
                        setOrder={setOrder}
                        asc={"PznAsc"}
                        desc={"PznDesc"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:product")}
                        order={order}
                        setOrder={setOrder}
                        asc={"ProductNameAsc"}
                        desc={"ProductNameDesc"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:artikeltext")}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={"AEP PS neu"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={"GEP PS neu"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:staffelpreis_1_neu")}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:menge_1_neu")}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={"SEK neu"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:agrosso") + " 1"}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:aep")}
                    />
                    <TableHeader
                        className={tableStyle.header}
                        label={t("table:gep")}
                    />

                    {loading && !data?.list && (
                        <TableContentLoader columns={11} rows={20} />
                    )}
                    {data?.list?.calculation?.results?.page.map((s, i) => (
                        <Fragment key={i}>
                            <p className={tableStyle.fullWidthCell}>
                                {s?.pzn || s?.product?.pzn}
                            </p>
                            <p className={tableStyle.cell}>
                                {s?.product?.name || "-"}
                            </p>
                            <p className={tableStyle.cell}>
                                {s?.artikeltext || "-"}
                            </p>
                            {/* Editable AEP PS neu */}
                            <p
                                className={cn(
                                    tableStyle.cell,
                                    tableStyle.editCell,
                                )}
                                onClick={() =>
                                    setEditRow({
                                        id: s?.id,
                                        field: "aep",
                                    })
                                }
                            >
                                {s?.id === editRow?.id &&
                                editRow?.field === "aep" ? (
                                    <input
                                        type="number"
                                        autoFocus={true}
                                        value={
                                            changes[s?.id]?.aepPsNeu ??
                                            s?.manualAdjustment?.AEPPsNeu ??
                                            s?.AEPPsNeu ??
                                            ""
                                        }
                                        className={inputStyle.text}
                                        placeholder={
                                            s?.manualAdjustment?.AEPPsNeu ??
                                            s?.AEPPsNeu
                                        }
                                        onBlur={() => setEditRow(null)}
                                        onChange={(evt) =>
                                            setChanges({
                                                ...changes,
                                                [s?.id]: {
                                                    aepPsNeu: clampOrEmpty(
                                                        evt.target.value.trim(),
                                                        0,
                                                        99999.99,
                                                    ),
                                                    gepPsNeu:
                                                        changes[s?.id]
                                                            ?.gepPsNeu ??
                                                        s?.manualAdjustment
                                                            ?.GEPPsNeu,
                                                },
                                            })
                                        }
                                    />
                                ) : (
                                    currencyFormat.format(
                                        changes[s?.id]?.aepPsNeu ??
                                            s?.manualAdjustment?.AEPPsNeu ??
                                            s?.AEPPsNeu,
                                    )
                                )}
                            </p>
                            {/* Editable GEP PS neu */}
                            <p
                                className={cn(
                                    tableStyle.cell,
                                    tableStyle.editCell,
                                )}
                                onClick={() =>
                                    setEditRow({
                                        id: s?.id,
                                        field: "gep",
                                    })
                                }
                            >
                                {s?.id === editRow?.id &&
                                editRow?.field === "gep" ? (
                                    <input
                                        type="number"
                                        autoFocus={true}
                                        value={
                                            changes[s?.id]?.gepPsNeu ??
                                            s?.manualAdjustment?.GEPPsNeu ??
                                            s?.GEPPsNeu ??
                                            ""
                                        }
                                        className={inputStyle.text}
                                        placeholder={
                                            s?.manualAdjustment?.GEPPsNeu ??
                                            s?.GEPPsNeu
                                        }
                                        onBlur={() => setEditRow(null)}
                                        onChange={(evt) =>
                                            setChanges({
                                                ...changes,
                                                [s?.id]: {
                                                    aepPsNeu:
                                                        changes[s?.id]
                                                            ?.aepPsNeu ??
                                                        s?.manualAdjustment
                                                            ?.AEPPsNeu,
                                                    gepPsNeu: clampOrEmpty(
                                                        evt.target.value.trim(),
                                                        0,
                                                        99999.99,
                                                    ),
                                                },
                                            })
                                        }
                                    />
                                ) : (
                                    currencyFormat.format(
                                        changes[s?.id]?.gepPsNeu ??
                                            s?.manualAdjustment?.GEPPsNeu ??
                                            s?.GEPPsNeu,
                                    )
                                )}
                            </p>
                            <p className={tableStyle.cell}>
                                {currencyFormat.format(s?.staffelpreis1Neu)}
                            </p>
                            <p className={tableStyle.cell}>
                                {integerFormatter.format(s?.menge1Neu)}
                            </p>
                            <p className={tableStyle.cell}>
                                {currencyFormat.format(s?.SEKNeu)}
                            </p>
                            <p className={tableStyle.cell}>
                                {currencyFormat.format(s?.agrosso1)}
                            </p>
                            <p className={tableStyle.cell}>
                                {currencyFormat.format(s?.AEP)}
                            </p>
                            <p
                                className={cn(
                                    tableStyle.cell,
                                    tableStyle.lastCell,
                                )}
                            >
                                {currencyFormat.format(s?.GEP)}
                            </p>
                        </Fragment>
                    ))}
                </div>
                <Pagination
                    onPageChange={(page) => setPage(page)}
                    pagination={data?.list?.calculation?.results}
                    loading={loading && !data?.list}
                />
            </div>
        </div>
    )
}
